import React from "react";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import AccordionCard from "../../../../../components/common/PL/AccordionCard/AccordionCard";
import BundleKey from "../../../../../translation/bundleKey";
import FormTextInput from "../../../../../components/common/PL/TextInput/FormTextInput";
import FormSelect from "../../../../../components/common/PL/Select/FormSelect";
import ObservationsTextArea from "./ObservationsTextArea/ObservationsTextArea";
import ObservationsImagePreview from "./ObservationsImagePreview/ObservationsImagePreview";
import ObservationsMultiImageUpload from "./ObservationsMultiImageUpload/ObservationsMultiImageUpload";
import ObservationsReadOnlyImageStrip from "./ObservationsReadOnlyImageStrip/ObservationsReadOnlyImageStrip";
import "./ObservationsForm.css";
import { RegistrationMode } from "../../registroIngresoNavigation";
import { Controller, useFormContext } from "react-hook-form";
import { EntryRegistrationFormValues } from "../types";
import { useTransportFieldLock } from "../AccessTypeForm/TransportContentForm/useTransportAuthPrefill";
import {
  PAIS_ARGENTINA_ID,
  paisesLatamOptions,
  provinciasArgentinaOptions,
} from "../../../../../utils/constants/locationConstants";
import useAppText, { AppTextKey } from "../../../../../hooks/useAppText";

type ObservationsFormProps = {
  formId: string;
  isTransport: boolean;
  mode: RegistrationMode;
  isSearching?: boolean;
};

const ObservationsForm = ({ formId, isTransport, mode, isSearching }: ObservationsFormProps) => {
  const { watch, setValue, control } = useFormContext<EntryRegistrationFormValues>();
  const inEventComment = watch("inEventComment");
  // Campos que vienen impuestos por la autorización de transporte seleccionada (siempre false
  // fuera del flujo de transportistas).
  const isLocked = useTransportFieldLock();

  const isExit = mode === "exit";

  // El remito se pide tanto en carga como en descarga; sólo al ingresar el guardia puede declarar
  // que el camión no trae remito, y en ese caso el evento viaja con `receipt: null`.
  const remitoNoAplica = !!watch("transportRemitoNoAplica");

  // El país es el de procedencia en entrada y el de destino en salida; sólo Argentina tiene
  // provincias cargadas, así que al elegir cualquier otro país la provincia deja de aplicar.
  const transportPais = watch("transportPais");
  const isArgentina = transportPais === PAIS_ARGENTINA_ID;

  // Requisitos que el guardia tiene que verificar antes de dejar entrar al camión. El texto lo
  // configura cada edificio, así que si no lo tiene cargado no hay nada que confirmar y el bloque
  // no se muestra.
  const accessRequirementText = useAppText(AppTextKey.ACCESS_REQUIREMENT_CHECK);

  React.useEffect(() => {
    if (!isArgentina) setValue("transportProvincia", "");
  }, [isArgentina, setValue]);

  // Las dos columnas del egreso son iguales para transportistas y para el resto de los flujos: la
  // del ingreso es de sólo lectura y la del egreso es la que completa el guardia ahora.
  const renderIngresoColumn = () => (
    <div className="observationsColumn ingreso">
      <h4 className="columnTitle">Ingreso</h4>
      <ObservationsTextArea
        name="inEventComment"
        label="Observaciones exclusivas del acceso"
        placeholder="-completar-"
        disabled
      />
      {!isSearching && (
        <ObservationsReadOnlyImageStrip
          names={["inEventPhoto", "inEventPhoto2", "inEventPhoto3"]}
          label="Imágenes referentes a la observación del acceso"
        />
      )}
    </div>
  );

  const renderEgresoColumn = () => (
    <div className="observationsColumn egreso">
      <h4 className="columnTitle">Egreso</h4>
      <ObservationsTextArea
        name="observationsText"
        label="Observaciones exclusivas de la salida"
        placeholder="-completar-"
      />
      <ObservationsMultiImageUpload
        names={["observationsPhoto", "observationsPhoto2", "observationsPhoto3"]}
        fileNames={[
          "Foto observación salida.",
          "Foto observación salida 2.",
          "Foto observación salida 3.",
        ]}
        label="Imágenes referentes a la observación de la salida"
      />
    </div>
  );

  return (
    <AccordionCard
      id={`${formId}-observations`}
      titleKey={BundleKey.DETAIL_ENTRYMETHOD_OBSERVATION_TITLE}
      number={isTransport ? 4 : 5}
      expanded
    >
      <div className="observationsFormContainer">
        {!isTransport && (
          <div className="observationsTopRow">
            <ObservationsTextArea
              name="peopleComment"
              label="Observaciones permanentes sobre este usuario"
              placeholder="-completar-"
            />
          </div>
        )}

        {isTransport ? (
          <>
            {/* En egreso la foto suelta se reemplaza por las columnas Ingreso/Egreso de abajo, así
                que los campos de transporte ocupan el ancho completo. */}
            <div className={isExit ? "observationsColumn" : "observationsTransportGrid"}>
              {/* En egreso los campos tienen todo el ancho para ellos: se acomodan en tres
                  columnas en vez de estirarse uno debajo del otro. */}
              <div className={`observationsTransportFields${isExit ? " threeColumns" : ""}`}>
                <div className="observationsRemitoRow">
                  <FormTextInput
                    name="transportNumeroRemito"
                    label="Número de remito"
                    placeholder="-completar-"
                    extraType="alfanumeric"
                    disabled={isLocked("transportNumeroRemito") || remitoNoAplica}
                  />
                  {/* Sólo al ingresar: en egreso el remito lo impone la autorización. */}
                  {!isExit && (
                    <Controller
                      name="transportRemitoNoAplica"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          className="observationsRemitoNoAplica"
                          data-field-name="transportRemitoNoAplica"
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                if (e.target.checked) setValue("transportNumeroRemito", "");
                              }}
                              disabled={isLocked("transportNumeroRemito")}
                            />
                          }
                          label="No aplica"
                        />
                      )}
                    />
                  )}
                </div>

                <FormTextInput
                  name="transportPrecinto"
                  label="Precinto"
                  placeholder="-completar-"
                  extraType="alfanumeric"
                  disabled={isLocked("transportPrecinto")}
                />

                <FormTextInput
                  name="transportPallets"
                  label="Pallets / Bultos"
                  placeholder="-completar-"
                  extraType="numeric"
                />

                <div className="observationsPaisProvinciaRow">
                  <FormSelect
                    name="transportPais"
                    label={isExit ? "País de destino" : "País de procedencia"}
                    placeholder="-completar-"
                    options={paisesLatamOptions}
                    isSearchable
                  />
                  {isArgentina && (
                    <FormSelect
                      name="transportProvincia"
                      label="Provincia"
                      placeholder="-completar-"
                      options={provinciasArgentinaOptions}
                      isSearchable
                    />
                  )}
                </div>

                {/* Sólo en ingreso: al egresar el camión ya pasó por este control. */}
                {!isExit && !!accessRequirementText && (
                  <Controller
                    name="accessRequirementCheck"
                    control={control}
                    defaultValue={false}
                    rules={{ required: "Confirmar los requisitos de acceso" }}
                    render={({ field, fieldState }) => (
                      <div
                        className="observationsAccessRequirement"
                        data-field-name="accessRequirementCheck"
                      >
                        <span className="observationsAccessRequirementLabel">
                          Requisitos de acceso <span className="observationsRequiredMark">*</span>
                        </span>
                        <div className="observationsAccessRequirementRow">
                          <p
                            className={`observationsAccessRequirementText${
                              fieldState.error ? " hasError" : ""
                            }`}
                          >
                            {accessRequirementText}
                          </p>
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            inputProps={{ "aria-label": "Requisitos de acceso" }}
                          />
                        </div>
                        {fieldState.error && (
                          <span className="observationsAccessRequirementError">
                            {fieldState.error.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                )}

                <Controller
                  name="mercaderiaEnTransito"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      data-field-name="mercaderiaEnTransito"
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={isLocked("mercaderiaEnTransito")}
                        />
                      }
                      label="Mercadería en tránsito"
                    />
                  )}
                />
              </div>

              {/* Sólo en ingreso: en egreso escribe sobre `observationsPhoto`, igual que el
                  uploader de la columna de egreso, y se pisarían entre sí. La nota de ingreso
                  acompaña a la foto en esta columna para aprovechar el hueco que dejaba debajo.
                  En egreso el guardia escribe en la columna "Egreso" de abajo. */}
              {!isExit && (
                <div className="observationsTransportSideColumn">
                  <ObservationsImagePreview
                    name="observationsPhoto"
                    fileName="Foto observación entrada."
                  />
                  <ObservationsTextArea
                    name="transportNotasIngreso"
                    label="Notas del Ingreso"
                    placeholder="-completar-"
                    disabled={isLocked("transportNotasIngreso")}
                  />
                </div>
              )}
            </div>

            {isExit && (
              <div className="observationsFormGrid exitMode">
                {renderIngresoColumn()}
                {renderEgresoColumn()}
              </div>
            )}
          </>
        ) : (
          <div className={`observationsFormGrid ${isExit ? "exitMode" : ""}`}>
            {isExit ? (
              renderIngresoColumn()
            ) : (
              <div className="observationsColumn ingreso">
                <ObservationsTextArea
                  name="observationsText"
                  label="Observaciones exclusivas del acceso"
                  placeholder="-completar-"
                />
                <ObservationsMultiImageUpload
                  names={["observationsPhoto", "observationsPhoto2", "observationsPhoto3"]}
                  fileNames={[
                    "Foto observación entrada.",
                    "Foto observación entrada 2.",
                    "Foto observación entrada 3.",
                  ]}
                  label="Imágenes referentes a la observación del acceso"
                />
              </div>
            )}

            {isExit && renderEgresoColumn()}
          </div>
        )}
      </div>
    </AccordionCard>
  );
};

export default ObservationsForm;
